import React, { useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Gavel, Swords, Clock, ListOrdered, Sparkles, Zap } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { runAiJob } from '../../lib/aiJob';

export const JUDGE = { estricto: ['Estricto', 'Exige plazos y prueba al detalle'], formalista: ['Formalista', 'Revisa requisitos, admisibilidad y nulidades'], garantista: ['Garantista', 'Protege derechos y debido proceso'] };
export const OPP = { agresiva: ['Agresiva', 'Ataca cada debilidad e interpone incidentes'], tecnica: ['Técnica', 'Norma, jurisprudencia y prueba precisas'], acuerdo: ['Busca acuerdo', 'Litiga pero ofrece conciliar'] };
const SURPRISE = { realista: ['Realistas · 1 o 2 por juicio', 'Donde la contraparte probablemente los plantearía'], presion: ['Alta presión · todas las etapas', 'Una sorpresa en cada etapa desde la segunda'] };

const Picker = ({ icon: Icon, title, options, value, onChange, suggestion, testid, note }) => (
  <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-3" data-testid={testid}>
    <p className="flex items-center gap-2 text-xs font-semibold text-white"><Icon className="h-4 w-4 text-amber-400" />{title}</p>
    {note && <p data-testid={`${testid}-real`} className="mt-1 text-[11px] text-sky-300">{note}</p>}
    <p data-testid={`${testid}-reason`} className="mt-1 text-[11px] text-slate-400"><Sparkles className="mr-1 inline h-3 w-3 text-amber-300" />{suggestion.source === 'juez_real' ? 'Según el juez asignado' : 'Sugerido por la IA'}: {options[suggestion.tipo][0]}{suggestion.motivo ? ` — ${suggestion.motivo}` : ''}</p>
    <div className="mt-2 grid gap-1.5 sm:grid-cols-3">
      {Object.entries(options).map(([k, [label, desc]]) => (
        <button key={k} data-testid={`${testid}-${k}`} aria-pressed={value === k} onClick={() => onChange(k)}
          className={`rounded-lg border px-2.5 py-2 text-left transition-colors ${value === k ? 'border-amber-500 bg-amber-500/10' : 'border-slate-800 hover:border-slate-600'}`}>
          <p className="text-xs font-semibold text-white">{label}{k === suggestion.tipo && <span className="ml-1 text-[10px] font-normal text-amber-300">· sugerido</span>}</p>
          <p className="text-[10px] text-slate-400">{desc}</p>
        </button>
      ))}
    </div>
  </div>
);

const Summary = ({ p }) => (
  <div data-testid="trial-process-summary" className="rounded-xl border border-sky-500/30 bg-sky-500/5 p-4 space-y-2">
    <p title={p.map.procedimiento} className="text-sm font-semibold text-white line-clamp-2">{p.map.procedimiento}</p>
    <p className="text-xs text-slate-300">{p.map.resumen}</p>
    {p.map.duracion && <p className="flex items-center gap-1.5 text-[11px] text-slate-400"><Clock className="h-3 w-3" />Duración estimada: {p.map.duracion}</p>}
    <ol className="max-h-44 space-y-1 overflow-y-auto pt-1 pr-1" data-testid="trial-summary-stages">
      {p.stages.map((st, i) => <li key={i} className="text-xs text-slate-300"><span className="font-semibold text-white">{i + 1}. {st.nombre}</span> <span className="text-slate-500">· {st.plazo}</span> — {st.que_pasa}</li>)}
    </ol>
  </div>
);

// Before starting: AI process summary + likely judge/opponent profiles (editable)
export const TrialSetup = ({ matter, onStarted }) => {
  const { api } = useAuth();
  const [p, setP] = useState(null);
  const [judge, setJudge] = useState('');
  const [opp, setOpp] = useState('');
  const [mode, setMode] = useState('realista');
  const [busy, setBusy] = useState('');
  const run = async (label, fn) => { setBusy(label); try { return await fn(); } catch (e) { toast.error(e.message); return null; } finally { setBusy(''); } };
  const prepare = () => run('La IA lee el caso y arma el resumen del proceso (≈1 min)...', async () => {
    const r = await runAiJob(api, `/trial-sim/matters/${matter.id}/preview`, {}, { timeout: 300000 });
    setP(r); setJudge(r.suggested.judge.tipo); setOpp(r.suggested.opponent.tipo);
  });
  const start = () => run('Comienza el juicio...', async () => onStarted(await runAiJob(api, `/trial-sim/matters/${matter.id}/start`, { preview_id: p.id, judge_type: judge, opponent_type: opp, surprise_mode: mode }, { timeout: 300000 })));
  const Btn = ({ onClick, testid, children }) => (
    <button data-testid={testid} onClick={onClick} disabled={!!busy} className="w-full rounded-lg bg-amber-500 py-3 text-sm font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-60 flex items-center justify-center gap-2">
      {busy ? <><Loader2 className="h-4 w-4 animate-spin" />{busy}</> : children}
    </button>
  );
  if (!p) return <Btn testid="trial-prepare-btn" onClick={prepare}><ListOrdered className="h-4 w-4" />Preparar simulación: resumen del proceso y perfiles</Btn>;
  const real = p.suggested.judge.source === 'juez_real';
  return (
    <div className="space-y-3" data-testid="trial-setup">
      <Summary p={p} />
      <Picker icon={Gavel} title="Tipo de juez" options={JUDGE} value={judge} onChange={setJudge} suggestion={p.suggested.judge} testid="trial-judge-picker"
        note={real ? `Juez real asignado: ${p.suggested.judge.judge_name} · perfil según su cargo y fallos registrados en la plataforma` : null} />
      <Picker icon={Swords} title="Estilo de la contraparte" options={OPP} value={opp} onChange={setOpp} suggestion={p.suggested.opponent} testid="trial-opponent-picker" />
      <div data-testid="trial-surprise-picker" className="rounded-xl border border-slate-800 bg-slate-900/60 p-3">
        <p className="flex items-center gap-2 text-xs font-semibold text-white"><Zap className="h-4 w-4 text-red-400" />Incidentes sorpresa</p>
        <p className="mt-1 text-[11px] text-slate-400">La contraparte presentará pruebas, objeciones o nulidades que no verás venir.</p>
        <div className="mt-2 grid gap-1.5 sm:grid-cols-2">
          {Object.entries(SURPRISE).map(([k, [label, desc]]) => (
            <button key={k} data-testid={`trial-surprise-${k}`} aria-pressed={mode === k} onClick={() => setMode(k)}
              className={`rounded-lg border px-2.5 py-2 text-left transition-colors ${mode === k ? 'border-red-500 bg-red-500/10' : 'border-slate-800 hover:border-slate-600'}`}>
              <p className="text-xs font-semibold text-white">{label}</p><p className="text-[10px] text-slate-400">{desc}</p>
            </button>
          ))}
        </div>
      </div>
      <Btn testid="trial-start-btn" onClick={start}>Iniciar simulación del juicio</Btn>
    </div>
  );
};
